import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Skeleton, Table, Tag, Upload, message } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { downloadFile, errorMessage } from '../../api/client';
import {
  fetchFixedInvestmentImageUrl,
  fetchFixedWithdrawalImageUrl,
  useFixedAccount,
  useUploadFixedInvestmentImage,
  useUploadFixedWithdrawalImage,
} from '../../api/hooks';
import type { PartyType } from '../../types';
import { date, money } from '../../utils/format';
import TransactionImageLink from '../investor/TransactionImageLink';

interface ReceiptRow {
  key: string;
  date: string;
  type: 'Investment' | 'Withdrawal';
  refId: number;
  reference: string;
  amount: number;
  url: string;
  fileName: string;
  hasTransactionImage: boolean;
}

const TYPE_COLOR: Record<ReceiptRow['type'], string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
};

interface Props {
  partyType: PartyType;
  partyId: number;
}

/** Every fixed-return transaction PDF in one place — mirrors the percentage Receipts tab. */
export default function FixedReceiptsTab({ partyType, partyId }: Props) {
  const { data: account, isLoading, error } = useFixedAccount(partyType, partyId);
  const uploadInvestmentImage = useUploadFixedInvestmentImage(partyType, partyId);
  const uploadWithdrawalImage = useUploadFixedWithdrawalImage(partyType, partyId);

  if (error) return <Alert type="error" showIcon message={errorMessage(error)} />;
  if (isLoading || !account) return <Skeleton active paragraph={{ rows: 6 }} />;

  const rows: ReceiptRow[] = [
    ...account.investments.map((i) => ({
      key: `fi-${i.id}`,
      date: i.investDate,
      type: 'Investment' as const,
      refId: i.id,
      reference: `FI-R-${String(i.id).padStart(6, '0')}`,
      amount: i.amount,
      url: `/receipts/fixed-investment/${i.id}`,
      fileName: `fixed_investment_receipt_${i.id}.pdf`,
      hasTransactionImage: i.hasTransactionImage,
    })),
    ...account.withdrawals.map((w) => ({
      key: `fw-${w.id}`,
      date: w.withdrawDate,
      type: 'Withdrawal' as const,
      refId: w.id,
      reference: `FW-R-${String(w.id).padStart(6, '0')}`,
      amount: w.amount,
      url: `/receipts/fixed-withdrawal/${w.id}`,
      fileName: `fixed_withdrawal_receipt_${w.id}.pdf`,
      hasTransactionImage: w.hasTransactionImage,
    })),
  ].sort((a, b) => b.date.localeCompare(a.date));

  const upload = (row: ReceiptRow) => async (file: RcFile) => {
    try {
      if (row.type === 'Investment') {
        await uploadInvestmentImage.mutateAsync({ investmentId: row.refId, file });
      } else {
        await uploadWithdrawalImage.mutateAsync({ withdrawalId: row.refId, file });
      }
      message.success('Transaction proof uploaded.');
    } catch (err) {
      message.error(errorMessage(err));
    }
    return Upload.LIST_IGNORE;
  };

  return (
    <Table<ReceiptRow>
      rowKey="key"
      dataSource={rows}
      scroll={{ x: 'max-content' }}
      locale={{ emptyText: 'No transactions yet' }}
      columns={[
        { title: 'Date', dataIndex: 'date', render: date },
        {
          title: 'Type',
          dataIndex: 'type',
          render: (value: ReceiptRow['type']) => <Tag color={TYPE_COLOR[value]}>{value}</Tag>,
        },
        { title: 'Receipt no', dataIndex: 'reference' },
        { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
        {
          title: 'Download',
          render: (_, row) => (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => downloadFile(row.url, row.fileName).catch((err) => message.error(errorMessage(err)))}
            >
              PDF
            </Button>
          ),
        },
        {
          title: 'Transaction proof',
          render: (_, row) =>
            row.hasTransactionImage ? (
              <TransactionImageLink
                exists
                title={`Transaction proof — ${row.reference}`}
                fetchUrl={() =>
                  row.type === 'Investment'
                    ? fetchFixedInvestmentImageUrl(partyType, partyId, row.refId)
                    : fetchFixedWithdrawalImageUrl(partyType, partyId, row.refId)
                }
              />
            ) : (
              <Upload accept=".jpg,.jpeg,.png,.webp" showUploadList={false} beforeUpload={upload(row)}>
                <Button type="link" size="small" icon={<UploadOutlined />}>
                  Upload
                </Button>
              </Upload>
            ),
        },
      ]}
    />
  );
}
